/**
 * OverlayBackdropContext - Single shared backdrop for all overlays.
 *
 * Instead of each modal/slideout rendering its own backdrop element,
 * this context provides one shared `<div>` that shows when ANY overlay
 * is active. Overlay sources (modal stack, slideout stack) register via
 * `useOverlayBackdrop(isActive)` and the backdrop appears when the
 * registration count is > 0.
 *
 * @example
 * ```tsx
 * // In Root.tsx
 * <OverlayBackdropProvider>
 *   <App />
 * </OverlayBackdropProvider>
 *
 * // In any overlay source
 * useOverlayBackdrop(isOpen); // registers when true, unregisters when false
 * ```
 */

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

/* eslint-disable react-refresh/only-export-components -- standard context pattern: provider, hook, and context co-located */
import { cx } from '@/utils/cx';

export interface OverlayBackdropContextValue {
  /** Whether the backdrop is currently active (any overlay is open) */
  isActive: boolean;
  /** Register an overlay source. Returns an unregister function. */
  register: () => () => void;
}

const OverlayBackdropContext =
  createContext<OverlayBackdropContextValue | null>(null);

/**
 * Hook for overlay sources (modal stack, slideout, etc.) to register
 * with the shared backdrop. When `isActive` is true, the backdrop shows.
 *
 * Safe to use outside a provider — acts as a no-op.
 */
export function useOverlayBackdrop(isActive: boolean): void {
  const ctx = useContext(OverlayBackdropContext);
  useEffect(() => {
    if (isActive && ctx) {
      return ctx.register();
    }
  }, [isActive, ctx]);
}

/** Z-index for the shared backdrop — below all overlay content (StackingContext starts at 400) */
const BACKDROP_Z_INDEX = 399;

export function OverlayBackdropProvider({ children }: { children: ReactNode }) {
  const [count, setCount] = useState(0);

  const register = useCallback(() => {
    setCount(c => c + 1);
    return () => setCount(c => c - 1);
  }, []);

  const value: OverlayBackdropContextValue = useMemo(
    () => ({
      isActive: count > 0,
      register,
    }),
    [count, register]
  );

  return (
    <OverlayBackdropContext.Provider value={value}>
      <div
        className={cx(
          'fixed inset-0 transition-opacity duration-200',
          count > 0
            ? 'bg-black/50 opacity-100'
            : 'opacity-0 pointer-events-none'
        )}
        style={{ zIndex: BACKDROP_Z_INDEX }}
        aria-hidden='true'
      />
      {children}
    </OverlayBackdropContext.Provider>
  );
}
